import {
  DEFAULT_LINK_MAX_BYTES,
  DEFAULT_LINK_MAX_FILE_COUNT,
  DEFAULT_LINK_MAX_MANIFEST_BYTES,
  DEFAULT_LINK_MAX_NAME_BYTES,
  DEFAULT_LINK_MAX_TYPE_BYTES,
  LINK_ENVELOPE_CONTENT_TYPE,
  LINK_ENVELOPE_MAGIC,
  LINK_PROTOCOL_VERSION,
} from "./constants";
import { copyToArrayBuffer, sha256Base64Url } from "./digest";

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder("utf-8", {
  fatal: true,
  ignoreBOM: false,
});
const envelopeMagic = textEncoder.encode(LINK_ENVELOPE_MAGIC);

export interface LinkInputFile {
  name: string;
  type: string;
  blob: Blob;
}

export interface LinkEnvelopeLimits {
  maxBytes: number;
  maxFileCount: number;
  maxManifestBytes: number;
  maxNameBytes: number;
  maxTypeBytes: number;
}

export interface PreparedLinkEnvelope {
  blob: Blob;
  fileCount: number;
  originalBytes: number;
  preparedBytes: number;
}

export interface DecryptedLinkFile {
  name: string;
  type: string;
  blob: Blob;
  sha256: string;
}

interface ManifestFile {
  name: string;
  type: string;
  size: number;
  offset: number;
  sha256: string;
}

interface EnvelopeManifest {
  v: typeof LINK_PROTOCOL_VERSION;
  files: ManifestFile[];
}

export const DEFAULT_LINK_ENVELOPE_LIMITS: LinkEnvelopeLimits = {
  maxBytes: DEFAULT_LINK_MAX_BYTES,
  maxFileCount: DEFAULT_LINK_MAX_FILE_COUNT,
  maxManifestBytes: DEFAULT_LINK_MAX_MANIFEST_BYTES,
  maxNameBytes: DEFAULT_LINK_MAX_NAME_BYTES,
  maxTypeBytes: DEFAULT_LINK_MAX_TYPE_BYTES,
};

function validateLimits(limits: LinkEnvelopeLimits): void {
  for (const [label, value] of Object.entries(limits)) {
    if (!Number.isSafeInteger(value) || value <= 0) {
      throw new Error(`${label} must be a positive safe integer.`);
    }
  }
}

function validateInputFile(file: LinkInputFile, limits: LinkEnvelopeLimits): void {
  const nameBytes = textEncoder.encode(file.name);
  const typeBytes = textEncoder.encode(file.type);
  if (nameBytes.byteLength === 0 || nameBytes.byteLength > limits.maxNameBytes) {
    throw new Error("A file name is empty or exceeds the configured limit.");
  }
  if (file.name.includes("\u0000")) {
    throw new Error("A file name contains an unsupported null character.");
  }
  if (typeBytes.byteLength > limits.maxTypeBytes) {
    throw new Error("A file type exceeds the configured limit.");
  }
  if (!Number.isSafeInteger(file.blob.size) || file.blob.size < 0) {
    throw new Error("A file has an invalid size.");
  }
}

function uint32Bytes(value: number): Uint8Array {
  const bytes = new Uint8Array(4);
  new DataView(bytes.buffer).setUint32(0, value, false);
  return bytes;
}

export async function prepareLinkEnvelope(
  files: readonly LinkInputFile[],
  limits: LinkEnvelopeLimits = DEFAULT_LINK_ENVELOPE_LIMITS,
): Promise<PreparedLinkEnvelope> {
  validateLimits(limits);
  if (files.length === 0 || files.length > limits.maxFileCount) {
    throw new Error("The selected file count exceeds the configured limit.");
  }

  let originalBytes = 0;
  let offset = 0;
  const manifestFiles: ManifestFile[] = [];

  for (const file of files) {
    validateInputFile(file, limits);
    originalBytes += file.blob.size;
    if (!Number.isSafeInteger(originalBytes) || originalBytes > limits.maxBytes) {
      throw new Error("The selected files exceed the configured byte limit.");
    }

    manifestFiles.push({
      name: file.name,
      type: file.type,
      size: file.blob.size,
      offset,
      sha256: await sha256Base64Url(file.blob),
    });
    offset += file.blob.size;
  }

  const manifest: EnvelopeManifest = {
    v: LINK_PROTOCOL_VERSION,
    files: manifestFiles,
  };
  const manifestBytes = textEncoder.encode(JSON.stringify(manifest));
  if (manifestBytes.byteLength > limits.maxManifestBytes) {
    throw new Error("The encrypted file manifest exceeds the configured limit.");
  }

  const blob = new Blob(
    [
      copyToArrayBuffer(envelopeMagic),
      copyToArrayBuffer(uint32Bytes(manifestBytes.byteLength)),
      copyToArrayBuffer(manifestBytes),
      ...files.map((file) => file.blob),
    ],
    { type: LINK_ENVELOPE_CONTENT_TYPE },
  );

  if (blob.size > limits.maxBytes) {
    throw new Error("The prepared files exceed the configured byte limit.");
  }

  return {
    blob,
    fileCount: files.length,
    originalBytes,
    preparedBytes: blob.size,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readManifestFile(value: unknown, limits: LinkEnvelopeLimits): ManifestFile {
  if (!isRecord(value)) {
    throw new Error("The encrypted file manifest is invalid.");
  }

  const { name, type, size, offset, sha256 } = value;
  if (
    typeof name !== "string" ||
    typeof type !== "string" ||
    typeof size !== "number" ||
    typeof offset !== "number" ||
    typeof sha256 !== "string"
  ) {
    throw new Error("The encrypted file manifest is invalid.");
  }

  validateInputFile({ name, type, blob: new Blob([new Uint8Array(0)]) }, limits);
  if (
    !Number.isSafeInteger(size) ||
    size < 0 ||
    !Number.isSafeInteger(offset) ||
    offset < 0
  ) {
    throw new Error("The encrypted file manifest has invalid file bounds.");
  }

  return { name, type, size, offset, sha256 };
}

function parseManifest(
  bytes: Uint8Array,
  limits: LinkEnvelopeLimits,
): EnvelopeManifest {
  let parsed: unknown;
  try {
    parsed = JSON.parse(textDecoder.decode(bytes)) as unknown;
  } catch {
    throw new Error("The encrypted file manifest cannot be read.");
  }

  if (
    !isRecord(parsed) ||
    parsed.v !== LINK_PROTOCOL_VERSION ||
    !Array.isArray(parsed.files) ||
    parsed.files.length === 0 ||
    parsed.files.length > limits.maxFileCount
  ) {
    throw new Error("The encrypted file manifest is invalid.");
  }

  return {
    v: LINK_PROTOCOL_VERSION,
    files: parsed.files.map((file) => readManifestFile(file, limits)),
  };
}

function hasMagic(bytes: Uint8Array, magic: Uint8Array): boolean {
  return magic.every((byte, index) => bytes[index] === byte);
}

export async function unpackLinkEnvelope(
  envelope: Blob,
  limits: LinkEnvelopeLimits = DEFAULT_LINK_ENVELOPE_LIMITS,
): Promise<DecryptedLinkFile[]> {
  validateLimits(limits);
  if (envelope.size > limits.maxBytes) {
    throw new Error("The encrypted file package exceeds the configured limit.");
  }

  const bytes = new Uint8Array(await envelope.arrayBuffer());
  const headerBytes = envelopeMagic.byteLength + 4;
  if (bytes.byteLength < headerBytes || !hasMagic(bytes, envelopeMagic)) {
    throw new Error("The encrypted file package has an unsupported format.");
  }

  const manifestLength = new DataView(
    bytes.buffer,
    bytes.byteOffset + envelopeMagic.byteLength,
    4,
  ).getUint32(0, false);
  if (
    manifestLength === 0 ||
    manifestLength > limits.maxManifestBytes ||
    headerBytes + manifestLength > bytes.byteLength
  ) {
    throw new Error("The encrypted file manifest has invalid bounds.");
  }

  const manifestEnd = headerBytes + manifestLength;
  const manifest = parseManifest(bytes.subarray(headerBytes, manifestEnd), limits);
  const payload = bytes.subarray(manifestEnd);

  let expectedOffset = 0;
  const files: DecryptedLinkFile[] = [];
  for (const file of manifest.files) {
    if (
      file.offset !== expectedOffset ||
      file.offset + file.size > payload.byteLength
    ) {
      throw new Error("The encrypted file package has invalid file bounds.");
    }

    const fileBytes = payload.slice(file.offset, file.offset + file.size);
    const digest = await sha256Base64Url(fileBytes);
    if (digest !== file.sha256) {
      throw new Error("A decrypted file failed integrity verification.");
    }

    files.push({
      name: file.name,
      type: file.type,
      blob: new Blob([copyToArrayBuffer(fileBytes)], {
        type: file.type || "application/octet-stream",
      }),
      sha256: digest,
    });
    expectedOffset += file.size;
  }

  if (expectedOffset !== payload.byteLength) {
    throw new Error("The encrypted file package contains unexpected bytes.");
  }

  return files;
}
